import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";

import { v4 as uuidv4 } from "uuid";
import _ from "lodash";
import { Loader } from "semantic-ui-react";
import { isNonEmptyArray } from "@cayuse/shared-react.utils.type-checks";
import SearchDropdown from "@cayuse/shared-react.components.search-dropdown";

import { useHasRoles } from "hooks";
import useDictionary from "hooks/useDictionary";
import {
    queryFunds,
    fetchFunds,
    fundToCardItems,
    mapFunds,
} from "utils";
import {
    DEFAULT_SEARCH_OPTIONS,
    ROLE_SP_AWARD_ADMINISTRATOR,
    ROLE_SP_AWARD_EDITOR,
    ROLE_SP_SYSTEM_ADMINISTRATOR,
} from "globals/constants";

import InfoCard from "components/common/InfoCard/InfoCard";
import infoCardStyles from "components/common/InfoCard/styles.css";

import styles from "./styles.css";
import { api } from "services";

const API_FUND_NUMBER_KEY = "accountId";
const DICTIONARY = {
    key: "fundManager",
    title: "Link to fund in Fund Manager",
    placeholder: "Search by fund number, name, or PI...",
    searchingMessage: "Loading...",
    nothingFound: "Nothing has been selected yet.",
};

const getDefaultRenderLabel = ({ tooltip, text }) => ({
    content: <span title={tooltip}>{text}</span>,
});

const FormFundsView = ({ url }) => {
    const {
        content: { award },
    } = useSelector((state) => state.award);
    const { awardId  } = award;
    const dictionary = useDictionary(DICTIONARY.key);
    const {
        [ROLE_SP_AWARD_ADMINISTRATOR]: isAwardAdmin,
        [ROLE_SP_SYSTEM_ADMINISTRATOR]: isSystemAdmin,
        [ROLE_SP_AWARD_EDITOR]: isAwardEditor,
    } = useHasRoles([
        ROLE_SP_AWARD_ADMINISTRATOR,
        ROLE_SP_SYSTEM_ADMINISTRATOR,
        ROLE_SP_AWARD_EDITOR,
    ]);

    const [searchField] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [selectedFunds, setSelectedFunds] = useState([]);
    const [prefetchedOptions, setPrefetchedOptions] = useState(DEFAULT_SEARCH_OPTIONS);

    const canEdit = useMemo(
        () => isAwardAdmin || isSystemAdmin || isAwardEditor,
        [isAwardAdmin, isSystemAdmin, isAwardEditor]
    );

    useEffect(() => {
        if (awardId) {
            api.funds.getAll(awardId).then((fmLinkedIds) => {
                if (isNonEmptyArray(fmLinkedIds)) {
                    fetchFunds(fmLinkedIds, url).then((funds) => {
                        setSelectedFunds(funds);
                        setIsLoading(false);
                    });
                } else {
                    setIsLoading(false);
                }
            });
        }
    }, [award.caseInstanceId]);

    const onFundSearchChange = (value, newFund) => {
        const newFunds = _.uniqBy(
            [...selectedFunds, mapFunds(newFund[0].fund)],
            API_FUND_NUMBER_KEY
        );

        setSelectedFunds(newFunds);
        api.funds.link(awardId, value);
        setPrefetchedOptions(DEFAULT_SEARCH_OPTIONS);
    };

    const onCloseFundView = (fund) => {
        const filteredFunds = _.without(selectedFunds, fund);
        setSelectedFunds(filteredFunds);
        api.funds.unlink(awardId, fund.accountId);
    };

    const search = async (searchText) => {
        setPrefetchedOptions([]);
        return queryFunds(searchText, url);
    };

    return (
        <>
            <div
                data-component="Containers\Awards\Detail\FormFundsView"
                className={styles.formFundsView}
            >
                <div>
                    <div className={styles.tables}>
                        <b>{dictionary.awardBurndown.awardBurndownTitle}</b>
                    </div>
                    <br />
                    <div className={styles.tables}>
                        {dictionary.awardBurndown.info}
                    </div>
                    <br />
                    <div className={`${styles.tables} ${styles.title}`}>
                        <b>
                            {dictionary.awardBurndown.title ?? DICTIONARY.title}
                        </b>
                    </div>
                    <br />
                    <div className={styles.searchDropdown}>
                        <SearchDropdown
                            value={searchField}
                            search={(options) => options}
                            selection
                            selectOnNavigation={false}
                            forceSelection={false}
                            selectOnBlur={false}
                            onGetDataPromise={search}
                            disabled={!canEdit}
                            searchingMessage={
                                dictionary.searchDropdown.searchingMessage ??
                                DICTIONARY.searchingMessage
                            }
                            placeholder={
                                dictionary.searchDropdown.placeholder ??
                                DICTIONARY.placeholder
                            }
                            onChange={onFundSearchChange}
                            renderLabel={getDefaultRenderLabel}
                            prefetchedOptions={prefetchedOptions}
                            multiple={false}
                            narrowCriteriaHint={{ show: true }}
                            minimumCharacters={3}
                            fieldLabel={dictionary.awardBurndown.title ?? DICTIONARY.title}
                            loadingIcon={
                                <span
                                    className="loading-indicator"
                                    aria-hidden="true"
                                >
                                    <span className="dot-flashing" />
                                </span>
                            }
                            icon={
                                <span className="caret-icon" aria-hidden="true">
                                    <svg
                                        height="20"
                                        width="20"
                                        viewBox="0 0 20 20"
                                        aria-hidden="true"
                                        focusable="false"
                                    >
                                        <path d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z"></path>
                                    </svg>
                                </span>
                            }
                            clearIcon={
                                <span className="clear-icon">
                                    <svg
                                        height="20"
                                        width="20"
                                        viewBox="0 0 20 20"
                                        aria-hidden="true"
                                        focusable="false"
                                    >
                                        <path d="M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c0.469 0.469 0.469 1.229 0 1.698z" />
                                    </svg>
                                </span>
                            }
                        />
                    </div>
                </div>
            </div>
            <div className={styles.formFundsView}>
                {isNonEmptyArray(selectedFunds) ? (
                    <div className={infoCardStyles.infocards}>
                        {selectedFunds.map((fund) => (
                            <div>
                                <InfoCard
                                    items={fundToCardItems(fund, dictionary)}
                                    key={uuidv4()}
                                    canEdit={canEdit}
                                    onClose={() => onCloseFundView(fund)}
                                />
                            </div>
                        ))}
                    </div>
                ) : (
                    <div>
                        {isLoading ? (
                            <Loader active size="tiny" inline />
                        ) : (
                            <i>
                                {dictionary.awardBurndown.nothingFound ??
                                    DICTIONARY.nothingFound}
                            </i>
                        )}
                    </div>
                )}
            </div>
        </>
    );
};

export default FormFundsView;
